import type { ReactNode, Ref } from 'react';

/**
 * Las piezas del Figma. Todas las pantallas se componen de aquí.
 *
 * ── SOLO TRES VARIABLES SON DEL CLIENTE ────────────────────────────────────────
 *
 * `--acento`, `--acento-2` y `--tipografia`. Son EXACTAMENTE las tres que escribe
 * `aplicarMarca` en `marca.ts` — ni una más. Cualquier otra cosa que se pinte con una variable
 * de marca sería una promesa que la marca blanca no cumple.
 *
 * Los colores de resultado (verde, rojo, ámbar) NO son configurables, y es deliberado: un
 * cliente con la marca roja no puede convertir "verificado" en rojo. El significado de un
 * color no se subcontrata.
 */

/** El acento del Figma. Es el defecto y funciona; no es un fallback triste. */
export const ACENTO = '#3332f2';
export const ACENTO_2 = '#6b5cff';

/**
 * La cabecera: la flecha de volver, la barra de pasos y la píldora "N de 3".
 *
 * ── LA BARRA TIENE TRES SEGMENTOS Y SIEMPRE SE VEN LOS TRES ────────────────────
 *
 * Una barra continua que se llena esconde cuánto falta. Tres segmentos dicen "son tres cosas,
 * vas por la segunda" antes de que la persona lea nada. En una pantalla que alguien abre una
 * sola vez en su vida, saber cuánto falta es lo que decide si sigue o se va.
 */
export function Pasos({
  paso,
  de = 3,
  onVolver,
  sobreVideo = false,
}: {
  paso: number;
  de?: number;
  onVolver?: (() => void) | undefined;
  sobreVideo?: boolean;
}) {
  const tinta = sobreVideo ? '#fff' : '#101114';
  const apagado = sobreVideo ? 'rgba(255,255,255,.28)' : 'rgba(16,17,20,.12)';

  return (
    <div className="flex items-center gap-3 px-5 pt-4">
      {onVolver ? (
        <button
          onClick={onVolver}
          aria-label="Volver"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
          style={{
            border: `1px solid ${sobreVideo ? 'rgba(255,255,255,.35)' : 'rgba(16,17,20,.14)'}`,
            color: tinta,
            background: sobreVideo ? 'rgba(0,0,0,.18)' : 'transparent',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M10 3 5 8l5 5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : (
        <span className="h-9 w-9 shrink-0" />
      )}

      <div className="flex flex-1 items-center gap-1.5">
        {Array.from({ length: de }, (_, i) => (
          <span
            key={i}
            className="h-[3px] flex-1 rounded-full transition-colors"
            style={{ background: i < paso ? 'var(--acento, #3332f2)' : apagado }}
          />
        ))}
      </div>

      <span
        className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
        style={{
          border: `1px solid ${sobreVideo ? 'rgba(255,255,255,.35)' : 'rgba(51,50,242,.35)'}`,
          color: sobreVideo ? '#fff' : 'var(--acento, #3332f2)',
          background: sobreVideo ? 'rgba(0,0,0,.18)' : 'transparent',
        }}
      >
        {paso} de {de}
      </span>
    </div>
  );
}

/**
 * El botón principal: degradado y la flecha en su círculo.
 *
 * El texto va en blanco SIEMPRE. Por eso `contrastaConBlanco` existe en el core y la API avisa
 * al cliente cuando su acento no llega a 4.5:1 — este es el botón del que hablaba ese aviso.
 */
export function Boton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="relative flex h-[58px] w-full items-center justify-center rounded-full text-[15px] font-semibold transition disabled:opacity-45"
      style={{
        background: 'linear-gradient(90deg, var(--acento, #3332f2), var(--acento-2, #6b5cff))',
        color: '#fff',
      }}
    >
      {children}
      <span
        className="absolute right-2 grid h-[42px] w-[42px] place-items-center rounded-full"
        style={{ background: 'rgba(255,255,255,.16)' }}
        aria-hidden
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M4 12 12 4M6 4h6v6"
            stroke="#fff"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

/** El secundario: contorno, sin degradado. Nunca compite con el principal. */
export function BotonSecundario({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex h-[54px] w-full items-center justify-center rounded-full text-[15px] font-semibold"
      style={{ border: '1.5px solid var(--acento, #3332f2)', color: 'var(--acento, #3332f2)' }}
    >
      {children}
    </button>
  );
}

/** La píldora "Seguro" del Figma, con su candado. */
export function Seguro({ texto = 'Seguro' }: { texto?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ background: 'rgba(51,50,242,.08)', color: 'var(--acento, #3332f2)' }}
    >
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
        <path
          d="M6 1 2 2.6v3.1C2 8.3 3.7 10.3 6 11c2.3-.7 4-2.7 4-5.3V2.6L6 1Z"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
      {texto}
    </span>
  );
}

/**
 * Las pastillas vivas del Figma: DNI · Luz · Nitidez.
 *
 * ── ESTO ES LO ÚNICO QUE LE EXPLICA A LA PERSONA POR QUÉ NO DISPARA ────────────
 *
 * Sin ellas, una captura automática que no captura es una pantalla rota: la persona sostiene
 * el DNI, no pasa nada, y no tiene forma de saber si es la luz, el pulso o que está apuntando
 * mal. Con ellas, ve cuál está en rojo y lo arregla sola en dos segundos.
 *
 * Decirle "hay poca luz" no le regala nada a un defraudador —él ya sabe iluminar— y le ahorra
 * el abandono a alguien honesto. Es la excepción a la regla de no explicar, y está razonada en
 * `calidad.ts`.
 */
export function Pastilla({ texto, ok }: { texto: string; ok: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold transition-colors"
      style={{
        background: ok ? 'rgba(255,255,255,.92)' : 'rgba(0,0,0,.45)',
        color: ok ? 'var(--acento, #3332f2)' : '#fff',
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: ok ? '#12A150' : '#F5A524' }}
        aria-hidden
      />
      {texto}
    </span>
  );
}

/**
 * El marco de esquinas del Figma, con el anillo de progreso.
 *
 * El anillo se llena con la racha de `encuadre.ts`: es la traducción visible de "sostenlo un
 * segundo más". Sin él, el segundo que tarda en dispararse parece que no pasa nada.
 *
 * ── `innerRef` NO ES DECORACIÓN ────────────────────────────────────────────────
 *
 * La detección busca la foto impresa del DNI EXACTAMENTE dentro de este recuadro — no en una
 * aproximación sobre el ancho del vídeo, que con `object-cover` es otra geometría (probado
 * con un DNI real: había que alejarlo para que entrara en la zona que el código creía que era
 * el marco). El ref expone el rect en pantalla; `deteccion.ts` lo mapea a coordenadas de
 * vídeo. Si el marco se mueve o cambia de tamaño, la detección lo sigue sola.
 */
export function Marco({
  progreso,
  forma = 'documento',
  innerRef,
  children,
}: {
  progreso: number;
  forma?: 'documento' | 'rostro';
  innerRef?: Ref<HTMLDivElement>;
  children?: ReactNode;
}) {
  const esquina = (pos: string, d: string) => (
    <svg
      className={`absolute ${pos}`}
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      aria-hidden
    >
      <path d={d} stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );

  return (
    <div
      ref={innerRef}
      className="relative mx-auto"
      style={
        forma === 'documento'
          ? { width: '86%', aspectRatio: '1.586' } // ISO/IEC 7810 ID-1: la proporción del DNI
          : { width: '68%', aspectRatio: '0.78' }
      }
    >
      {forma === 'rostro' ? (
        <div
          className="absolute inset-0 rounded-[50%] transition-colors"
          style={{ border: `2.5px solid ${progreso > 0 ? 'var(--acento, #3332f2)' : 'rgba(255,255,255,.7)'}` }}
        />
      ) : null}

      {esquina('left-0 top-0', 'M2 12V4a2 2 0 0 1 2-2h8')}
      {esquina('right-0 top-0', 'M28 12V4a2 2 0 0 0-2-2h-8')}
      {esquina('bottom-0 left-0', 'M2 18v8a2 2 0 0 0 2 2h8')}
      {esquina('bottom-0 right-0', 'M28 18v8a2 2 0 0 1-2 2h-8')}

      {/* El progreso: una línea que cruza el marco de izquierda a derecha. */}
      <span
        className="absolute bottom-[-10px] left-0 h-[3px] rounded-full transition-[width] duration-200"
        style={{ width: `${Math.round(progreso * 100)}%`, background: 'var(--acento, #3332f2)' }}
        aria-hidden
      />
      {children}
    </div>
  );
}

/**
 * "Powered by Valídate".
 *
 * `oculto` existe porque la marca blanca lo permite (v1 tiene `noPoweredBy=1`). Que sea una
 * prop y no un `if` perdido es para que se vea quién lo apaga.
 */
export function Firma({ oculto = false }: { oculto?: boolean }) {
  if (oculto) return null;
  return (
    <p className="pb-5 pt-4 text-center text-[11px]" style={{ color: 'rgba(16,17,20,.45)' }}>
      Powered by <span className="font-bold" style={{ color: '#E2333A' }}>valídate</span>
    </p>
  );
}

/** El lienzo de las pantallas claras. 440×956 en el Figma; aquí se estira al móvil real. */
export function Pantalla({ children }: { children: ReactNode }) {
  return (
    <div
      className="mx-auto flex min-h-[100dvh] w-full max-w-[440px] flex-col"
      style={{
        background: 'linear-gradient(180deg, #fff 60%, rgba(107,92,255,.07))',
        fontFamily: 'var(--tipografia, Inter, system-ui, sans-serif)',
        color: '#101114',
      }}
    >
      {children}
    </div>
  );
}
